import React, { useEffect, useRef, useCallback, useState } from "react";
import ReactTooltip, { Place } from "react-tooltip";
import { Text, TextTypeAndSize } from "@ifgengineering/component-library";
import { ColorType, theme } from "@styles/theme";
import {
  LabelText,
  StyledCross,
  StyledIcon,
  StyledTooltip,
  TitleText,
  TooltipText,
  TooltipWrapper,
} from "./styled";
import { IconType } from "@icon-park/react/es/all";

type TooltipRef = { tooltipRef: null } | null;

interface TooltipProps {
  text: string | React.ReactElement;
  textColor?: ColorType;
  label?: string;
  labelFont?: TextTypeAndSize;
  labelColor?: ColorType;
  hideLabel?: boolean;
  title?: string;
  titleColor?: ColorType;
  id?: string;
  backgroundColor?: ColorType;
  openOnMount?: boolean;
  icon?: IconType;
  iconColor?: ColorType;
  iconPosition?: "left" | "right";
  iconSize?: number;
  place?: Place;
}

const Tooltip: React.FC<TooltipProps> = ({
  id,
  icon,
  iconColor = "SLATE450",
  iconPosition = "left",
  iconSize = 16,
  backgroundColor = "WHITE",
  text,
  textColor = "SLATE500",
  label = "Important Information",
  labelFont = "H14",
  labelColor = "SLATE800",
  hideLabel,
  openOnMount,
  title,
  titleColor = "SLATE500",
  place = "bottom",
}) => {
  const [isTooltipOpen, setTooltipOpen] = useState(false);
  const tooltipWrapperRef = useRef(null);
  const tooltip = useRef(null);
  useEffect(() => {
    openOnMount && onTooltipTriggerClick();
  }, [tooltipWrapperRef.current]);

  const hideTooltipHandler = useCallback(() => {
    const tooltipWrapperEl = tooltipWrapperRef.current;
    if (tooltipWrapperEl) {
      ReactTooltip.hide(tooltipWrapperEl);
      setTooltipOpen(false);
      document.removeEventListener("click", hideTooltipHandler);
    }
  }, []);

  const onTooltipTriggerClick = useCallback(() => {
    const tooltipWrapperEl = tooltipWrapperRef.current;
    if (tooltipWrapperEl) {
      if (isTooltipOpen) {
        ReactTooltip.hide(tooltipWrapperEl);
        setTooltipOpen(false);
      } else {
        ReactTooltip.show(tooltipWrapperEl);
        setTooltipOpen(true);

        document.addEventListener("click", hideTooltipHandler);
      }
    }
  }, [isTooltipOpen]);

  useEffect(() => {
    return () => {
      document.removeEventListener("click", hideTooltipHandler);
    };
  }, []);

  const iconPos = iconPosition === "right" ? "row-reverse" : "row";

  return (
    <section>
      <div
        data-tip
        data-for={id}
        ref={tooltipWrapperRef}
        data-event="click"
        onClick={onTooltipTriggerClick}
        style={{
          display: "flex",
          gap: "4.75px",
          alignItems: "center",
          flexDirection: iconPos,
        }}
      >
        {icon && (
          <StyledIcon
            type={icon}
            size={iconSize}
            fill={theme.colors[iconColor]}
          />
        )}
        {label && !hideLabel && (
          <LabelText type={labelFont} color={labelColor}>
            {label}
          </LabelText>
        )}
      </div>
      <StyledTooltip
        ref={tooltip}
        overridePosition={(position: any) => ({
          top: position.top,
          left: window.innerWidth < 600 ? 4 : position.left,
        })}
        backgroundColor={theme.colors[backgroundColor]}
        id={id}
        place={place}
        type="light"
        isCapture
        clickable
        globalEventOff="hover"
        effect="solid"
        arrowColor={theme.colors[backgroundColor]}
        getContent={() => (
          <TooltipWrapper>
            <StyledCross
              fill={theme.colors.SLATE500}
              size={14}
              onClick={() => {
                const current: TooltipRef = tooltip.current;
                current!.tooltipRef = null;
                onTooltipTriggerClick();
              }}
            />
            <TooltipText>
              {title && (
                <TitleText type="H14" color={titleColor}>
                  {title}
                </TitleText>
              )}
              <Text type="P14" color={textColor} as="div">
                {text}
              </Text>
            </TooltipText>
          </TooltipWrapper>
        )}
      />
    </section>
  );
};

export default Tooltip;
